import { useStudyText } from '@/i18n/studyText';
import { useEffect, useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { adminUsersApi } from '@/features/workspace/api';
import { RoleCategoryEditor, accessCategoriesForRoles } from './RoleCategoryEditor';
import { OwnerAccessModal } from './OwnerAccessModal';
import type {
  AdminAuditEntry,
  AdminUserCatalog,
  AdminUserDetail,
  AdminUserListItem,
  CategoryAssignment,
  InvitationOut,
} from '@/features/owner/adminUsersTypes';
import { formatRoleLabel } from '@/lib/navigation';

function catalogLabel(
  items: Array<{ code: string; label: Record<string, string> }> | undefined,
  code: string,
  locale: string,
): string {
  const hit = items?.find((item) => item.code === code);
  return hit?.label?.[locale] || hit?.label?.ru || formatRoleLabel(code);
}

function statusChipClass(status: string): string {
  if (status === 'ACTIVE') return ' mint';
  if (status === 'SUSPENDED' || status === 'ARCHIVED') return ' red';
  return ' gold';
}

interface BaseModalProps {
  user: AdminUserListItem;
  onClose: () => void;
  onChanged: () => void;
}

export const UserDetailModal = ({ user, onClose }: BaseModalProps) => {
  const { t, locale } = useI18n();
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [catalog, setCatalog] = useState<AdminUserCatalog | null>(null);

  useEffect(() => {
    adminUsersApi.detail(user.id).then(setDetail).catch(() => undefined);
    adminUsersApi.catalog().then(setCatalog).catch(() => undefined);
  }, [user.id]);

  return (
    <OwnerAccessModal
      title={`${t('admin.users.modal.open')} · ${user.full_name}`}
      onClose={onClose}
    >
      {!detail ? (
        <div className="owner-access-empty">{t('common.loading')}</div>
      ) : (
        <>
          <div className="owner-access-grid2">
            <div className="owner-access-note">
              <strong>ID Academia</strong>
              <br />
              {detail.academia_id}
            </div>
            <div className="owner-access-note">
              <strong>Активный email</strong>
              <br />
              {detail.email}
            </div>
            <div className="owner-access-note">
              <strong>Статус</strong>
              <br />
              <span className={`owner-access-chip${statusChipClass(detail.status)}`}>
                {t(`admin.users.status.${detail.status}` as never)}
              </span>
              {detail.is_sole_system_owner ? (
                <>
                  <br />
                  <span className="owner-access-chip gold">{t('admin.users.soleOwner')}</span>
                </>
              ) : null}
            </div>
            <div className="owner-access-note">
              <strong>Роли</strong>
              <br />
              {detail.roles.length === 0
                ? '—'
                : detail.roles
                    .map((role) => catalogLabel(catalog?.roles, role, locale))
                    .join(', ')}
            </div>
          </div>

          <div className="owner-access-note">
            <strong>{t('admin.users.field.categories')}</strong>
            <br />
            {detail.category_assignments.length === 0
              ? '—'
              : detail.category_assignments.map((assignment) => (
                  <span key={assignment.category_code} className="owner-access-chip">
                    {catalogLabel(catalog?.categories, assignment.category_code, locale)}
                    {Object.keys(assignment.metadata).length > 0
                      ? ` · ${Object.values(assignment.metadata).filter(Boolean).join(', ')}`
                      : ''}
                  </span>
                ))}
          </div>

          <div className="owner-access-grid2">
            <div className="owner-access-note">
              <strong>{t('admin.users.field.createdAt')}</strong>
              <br />
              {new Date(detail.created_at).toLocaleString(locale)}
            </div>
            <div className="owner-access-note">
              <strong>{t('admin.users.field.lastLogin')}</strong>
              <br />
              {detail.last_login_at ? new Date(detail.last_login_at).toLocaleString(locale) : '—'}
            </div>
          </div>

          {detail.status_note ? (
            <div className="owner-access-note warn">
              {t('admin.users.field.statusNote')}: {detail.status_note}
            </div>
          ) : null}

          <div className="owner-access-note">
            В административном просмотре не показываются пароль, токены приглашения/сброса и другие
            закрытые учётные данные.
          </div>
        </>
      )}
    </OwnerAccessModal>
  );
};

interface EditModalProps extends BaseModalProps {
  catalog: AdminUserCatalog;
}

export const UserEditModal = ({ user, catalog, onClose, onChanged }: EditModalProps) => {
  const { t, locale } = useI18n();
  const { notify } = useToast();
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [position, setPosition] = useState('');
  const [department, setDepartment] = useState('');
  const [roles, setRoles] = useState<string[]>([]);
  const [categories, setCategories] = useState<CategoryAssignment[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminUsersApi.detail(user.id).then((data) => {
      setDetail(data);
      setFirstName(data.first_name ?? '');
      setLastName(data.last_name ?? '');
      setPosition(data.position ?? '');
      setDepartment(data.department ?? '');
      setRoles(data.roles);
      setCategories(accessCategoriesForRoles(data.roles, data.category_assignments));
    });
  }, [user.id]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await adminUsersApi.update(user.id, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        position: position.trim() || null,
        department: department.trim() || null,
        roles,
        categories,
        locale,
      });
      notify(t('admin.users.toast.updated'), 'success');
      onChanged();
      onClose();
    } catch (error) {
      notify(error instanceof Error ? error.message : t('common.error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <OwnerAccessModal
      title={`${t('admin.users.modal.edit')} · ${user.full_name}`}
      onClose={onClose}
      wide
      footer={
        detail ? (
          <>
            <button type="button" className="owner-access-btn" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="owner-access-btn gold"
              disabled={saving}
              onClick={() => void handleSubmit()}
            >
              {t('common.save')}
            </button>
          </>
        ) : null
      }
    >
      {!detail ? (
        <div className="owner-access-empty">{t('common.loading')}</div>
      ) : (
        <>
          <div className="owner-access-grid2">
            <label className="owner-access-field">
              <span>{t('admin.users.field.firstName')}</span>
              <input value={firstName} onChange={(event) => setFirstName(event.target.value)} />
            </label>
            <label className="owner-access-field">
              <span>{t('admin.users.field.lastName')}</span>
              <input value={lastName} onChange={(event) => setLastName(event.target.value)} />
            </label>
            <label className="owner-access-field">
              <span>{t('admin.users.field.position')}</span>
              <input value={position} onChange={(event) => setPosition(event.target.value)} />
            </label>
            <label className="owner-access-field">
              <span>{t('admin.users.field.department')}</span>
              <input value={department} onChange={(event) => setDepartment(event.target.value)} />
            </label>
          </div>

          <RoleCategoryEditor
            roles={catalog.roles}
            categories={catalog.categories}
            selectedRoles={roles}
            selectedCategories={categories}
            onRolesChange={setRoles}
            onCategoriesChange={setCategories}
            lockSystemOwner={detail.is_sole_system_owner}
            accessOnly
          />
          {detail.is_sole_system_owner ? (
            <div className="owner-access-note warn">{t('admin.users.soleOwnerHint')}</div>
          ) : null}
        </>
      )}
    </OwnerAccessModal>
  );
};

const NEXT_ACTIONS: Record<string, { action: string; labelKey: string; needsReason: boolean }[]> = {
  ACTIVE: [
    { action: 'SUSPEND', labelKey: 'admin.users.status.action.suspend', needsReason: true },
    { action: 'ARCHIVE', labelKey: 'admin.users.status.action.archive', needsReason: true },
  ],
  INVITED: [
    { action: 'SUSPEND', labelKey: 'admin.users.status.action.suspend', needsReason: true },
    { action: 'ARCHIVE', labelKey: 'admin.users.status.action.archive', needsReason: true },
  ],
  SUSPENDED: [
    { action: 'REACTIVATE', labelKey: 'admin.users.status.action.reactivate', needsReason: false },
  ],
  ARCHIVED: [
    { action: 'RESTORE', labelKey: 'admin.users.status.action.restore', needsReason: false },
  ],
};

export const UserStatusModal = ({ user, onClose, onChanged }: BaseModalProps) => {
  const { t } = useI18n();
  const { notify } = useToast();
  const [reason, setReason] = useState('');
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const options = NEXT_ACTIONS[user.status] ?? [];

  const run = async (action: string) => {
    if (action === 'ARCHIVE' && !confirmArchive) {
      setPendingAction(action);
      return;
    }
    setBusy(true);
    try {
      await adminUsersApi.setStatus(user.id, { action, reason: reason || undefined });
      notify(t('admin.users.toast.statusChanged'), 'success');
      onChanged();
      onClose();
    } catch (error) {
      notify(error instanceof Error ? error.message : t('common.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (user.is_sole_system_owner) {
    return (
      <OwnerAccessModal title={t('admin.users.modal.status')} onClose={onClose}>
        <div className="owner-access-note warn">{t('admin.users.soleOwnerHint')}</div>
      </OwnerAccessModal>
    );
  }

  return (
    <OwnerAccessModal
      title={t('admin.users.modal.status')}
      subtitle={`${t('admin.users.status.current')}: ${t(`admin.users.status.${user.status}` as never)}`}
      onClose={onClose}
      footer={
        <>
          {options.map((option) => (
            <button
              key={option.action}
              type="button"
              className={`owner-access-btn${option.action === 'ARCHIVE' ? ' danger' : ' gold'}`}
              disabled={
                busy ||
                (option.action === 'ARCHIVE' && pendingAction === 'ARCHIVE' && !confirmArchive)
              }
              onClick={() => void run(option.action)}
            >
              {t(option.labelKey as never)}
            </button>
          ))}
        </>
      }
    >
      {options.length === 0 ? <div className="owner-access-empty">{t('admin.users.status.none')}</div> : null}
      {options.some((option) => option.needsReason) ? (
        <label className="owner-access-field full">
          <span>{t('admin.users.status.reason')}</span>
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} />
        </label>
      ) : null}
      {pendingAction === 'ARCHIVE' ? (
        <label className="owner-access-roleopt">
          <input
            type="checkbox"
            checked={confirmArchive}
            onChange={(event) => setConfirmArchive(event.target.checked)}
          />
          <span>{t('admin.users.status.archiveConfirm')}</span>
        </label>
      ) : null}
    </OwnerAccessModal>
  );
};

export const UserMessageModal = ({ user, onClose, onChanged }: BaseModalProps) => {
  const { t } = useI18n();
  const { notify } = useToast();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    try {
      await adminUsersApi.sendMessage(user.id, { subject, body });
      notify(t('admin.users.toast.messageSent'), 'success');
      onChanged();
      onClose();
    } catch (error) {
      notify(error instanceof Error ? error.message : t('common.error'), 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <OwnerAccessModal
      title={t('admin.users.modal.message')}
      subtitle={`${t('admin.users.message.to')}: ${user.full_name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="owner-access-btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="owner-access-btn gold"
            disabled={sending}
            onClick={() => void handleSend()}
          >
            {t('admin.users.message.send')}
          </button>
        </>
      }
    >
      <label className="owner-access-field full">
        <span>{t('admin.users.message.subject')}</span>
        <input value={subject} onChange={(event) => setSubject(event.target.value)} />
      </label>
      <label className="owner-access-field full">
        <span>{t('admin.users.message.body')}</span>
        <textarea value={body} onChange={(event) => setBody(event.target.value)} />
      </label>
    </OwnerAccessModal>
  );
};

function invitationStatusLabel(status: string | null | undefined, noneLabel: string): string {
  if (!status) return noneLabel;
  if (status === 'PENDING') return 'Ожидает';
  if (status === 'ACCEPTED') return 'Принято';
  if (status === 'REVOKED') return 'Отозвано';
  if (status === 'EXPIRED') return 'Истекло';
  return status;
}

export const UserInvitationModal = ({ user, onClose, onChanged }: BaseModalProps) => {
  const st = useStudyText();
  const { t, locale } = useI18n();
  const { notify } = useToast();
  const [invitation, setInvitation] = useState<InvitationOut | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    adminUsersApi.invitation(user.id).then(setInvitation).catch(() => undefined);
  };

  useEffect(load, [user.id]);

  const resend = async () => {
    setBusy(true);
    try {
      const result = await adminUsersApi.resendInvitation(user.id, locale);
      setInvitation(result);
      notify(t(result.email_delivery === 'sent' ? 'admin.users.toast.invitationSent' : 'admin.users.toast.emailFailed'), result.email_delivery === 'sent' ? 'success' : 'error');
      onChanged();
    } catch (error) {
      notify(error instanceof Error ? error.message : t('common.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      const result = await adminUsersApi.revokeInvitation(user.id);
      setInvitation(result);
      notify(t('admin.users.toast.invitationRevoked'), 'success');
      onChanged();
    } catch (error) {
      notify(error instanceof Error ? error.message : t('common.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <OwnerAccessModal
      title={t('admin.users.modal.invitation')}
      onClose={onClose}
      footer={
        invitation ? (
          <>
            <button
              type="button"
              className="owner-access-btn danger"
              disabled={busy || invitation.status !== 'PENDING'}
              onClick={() => void revoke()}
            >
              {t('admin.users.invitation.revoke')}
            </button>
            <button
              type="button"
              className="owner-access-btn gold"
              disabled={busy}
              onClick={() => void resend()}
            >
              {t('admin.users.invitation.resend')}
            </button>
          </>
        ) : null
      }
    >
      {!invitation ? (
        <div className="owner-access-empty">{t('common.loading')}</div>
      ) : (
        <>
          <div className="owner-access-note">
            <strong>{t('admin.users.field.status')}</strong>
            <br />
            <span
              className={`owner-access-chip${
                invitation.status === 'PENDING' ? ' gold' : invitation.status === 'ACCEPTED' ? ' mint' : ''
              }`}
            >
              {st(invitationStatusLabel(invitation.status, t('admin.users.invitation.none')))}
            </span>
          </div>
          {invitation.email_delivery === 'not_delivered' ? (
            <div className="owner-access-note bad">
              {t('admin.users.invitation.notDelivered')}
              {invitation.delivery_detail ? `: ${invitation.delivery_detail}` : ''}
            </div>
          ) : invitation.email_delivery === 'failed' ? (
            <div className="owner-access-note bad">
              {invitation.delivery_detail || t('admin.users.toast.emailFailed')}
            </div>
          ) : invitation.email_delivery === 'sent' && invitation.status !== 'ACCEPTED' ? (
            <div className="owner-access-note">
              {t('admin.users.invitation.sentUnverified')}
            </div>
          ) : invitation.status === 'ACCEPTED' ? (
            <div className="owner-access-note good">{t('admin.users.invitation.acceptedVerified')}</div>
          ) : null}
          {invitation.expires_at ? (
            <div className="owner-access-note">
              {t('admin.users.invitation.expires')}:{' '}
              {new Date(invitation.expires_at).toLocaleString(locale)}
            </div>
          ) : null}
        </>
      )}
    </OwnerAccessModal>
  );
};

export const UserHistoryModal = ({ user, onClose }: BaseModalProps) => {
  const { t, locale } = useI18n();
  const [entries, setEntries] = useState<AdminAuditEntry[] | null>(null);

  useEffect(() => {
    adminUsersApi.history(user.id).then(setEntries).catch(() => setEntries([]));
  }, [user.id]);

  return (
    <OwnerAccessModal title={t('admin.users.modal.history')} onClose={onClose}>
      {!entries ? (
        <div className="owner-access-empty">{t('common.loading')}</div>
      ) : entries.length === 0 ? (
        <div className="owner-access-empty">{t('common.notFound')}</div>
      ) : (
        <div className="owner-access-history">
          {entries.map((entry) => (
            <article key={entry.id}>
              <strong>{entry.summary}</strong>
              {entry.note ? <div className="owner-access-note" style={{ marginTop: 6 }}>{entry.note}</div> : null}
              <small>
                {entry.actor_label} · {new Date(entry.created_at).toLocaleString(locale)}
              </small>
            </article>
          ))}
        </div>
      )}
    </OwnerAccessModal>
  );
};
